/**
 * Action Step Collector
 *
 * Converts Playwright TestStep trees into ActionStep arrays.
 * Filters to user-meaningful categories, calculates video offsets,
 * and preserves parent-child hierarchy.
 */

import type { ActionStep, ActionCategory } from '@testrelic/core';

/** Playwright step categories we include in the report. */
const INCLUDED_CATEGORIES = new Set(['pw:api', 'expect', 'test.step']);

/** Maps Playwright step category to our ActionCategory. */
function mapCategory(pwCategory: string): ActionCategory {
  if (pwCategory === 'expect') return 'assertion';
  if (pwCategory === 'test.step') return 'custom_step';
  return 'ui_action';
}

/**
 * Minimal shape of Playwright's TestStep that we consume.
 * Avoids importing Playwright types directly in this module.
 */
export interface PwStepLike {
  readonly title: string;
  readonly category: string;
  readonly startTime: Date;
  readonly duration: number;
  readonly error?: { message?: string } | undefined;
  readonly steps: readonly PwStepLike[];
}

/**
 * Recursively converts a Playwright step tree into ActionStep objects.
 *
 * When a step's category is included (pw:api, expect, test.step), it is
 * converted and its children are nested underneath it.
 *
 * When a step's category is excluded (hook, fixture, attach, etc.), the
 * step itself is skipped but its children are still traversed — any
 * included descendants are "promoted" into the output list. This ensures
 * actions inside beforeEach/afterEach hooks are captured.
 *
 * @param step - A single Playwright TestStep
 * @param testStartTime - Test result start time for video offset calculation
 * @param output - Array to push results into (for promoted children)
 */
function convertStep(
  step: PwStepLike,
  testStartTime: Date,
  output: ActionStep[],
): void {
  if (INCLUDED_CATEGORIES.has(step.category)) {
    const children: ActionStep[] = [];
    for (const child of step.steps) {
      convertStep(child, testStartTime, children);
    }

    const videoOffsetMs = step.startTime.getTime() - testStartTime.getTime();

    output.push({
      title: step.title,
      category: mapCategory(step.category),
      timestamp: step.startTime.toISOString(),
      duration: step.duration,
      videoOffset: videoOffsetMs >= 0 ? videoOffsetMs / 1000 : null,
      status: step.error ? 'failed' : 'passed',
      error: step.error?.message ?? null,
      children,
    });
  } else {
    // Skip this step but look through to its children —
    // promotes pw:api/expect/test.step steps inside hooks/fixtures.
    for (const child of step.steps) {
      convertStep(child, testStartTime, output);
    }
  }
}

/**
 * Collects action steps from a Playwright test result's step tree.
 *
 * Filters to only user-meaningful categories (pw:api, expect, test.step),
 * looks through excluded parents (hook, fixture, attach) to capture their
 * included children, calculates video offsets, and preserves nesting hierarchy.
 *
 * @param steps - Top-level Playwright steps from result.steps
 * @param testStartTime - Test result start time (for video offset)
 * @returns Chronologically ordered array of ActionStep objects
 */
export function collectActionSteps(
  steps: readonly PwStepLike[],
  testStartTime: Date,
): ActionStep[] {
  const result: ActionStep[] = [];
  for (const step of steps) {
    convertStep(step, testStartTime, result);
  }
  return result;
}
